import {Box, Flex, P, showMessage} from 'exsportia-components'
import {useFormikContext} from 'formik'
import {DateTime} from 'luxon'
import React, {FC, useCallback, useEffect, useMemo, useState} from 'react'
import {useQuery} from 'react-query'
import {useNavigate} from 'react-router-dom'

import {MemberQueries} from '../../api/member'
import {useSubscriptionBooking} from '../../api/subscription-bookings/hooks'
import {SubscriptionBookingStatusesEnum} from '../../api/subscription-bookings/types'
import {SUBSCRIPTION_DURATION_TYPE_MAP} from '../../api/subscriptions/constants'
import {useSubscription} from '../../api/subscriptions/hooks'
import {useVenue} from '../../api/venue/hooks'
import {Amenities} from '../../components/amenities/amenities'
import {SubscriptionPreviewCard} from '../../components/subscription-preview-card/subscription-preview-card'
import {noop} from '../../helpers/logic'
import {GridLayout} from '../../layout/grid-layout/grid-layout'
import {TranslatorScope} from '../../locale/types'
import {useAuthState} from '../../services/auth/hooks'
import {
  CyclePayTypeEnum,
  PaySubscriptionCycleFormType,
} from '../../services/subscription-bookings/forms/pay-subscription-cycles-form'
import {useUser} from '../../services/user/hooks/use-user'
import {Button} from '../../ui/components/button/button'
import {DividerWithContent} from '../../ui/components/divider-with-content/divider-with-content'
import {ExsportiaIcon} from '../../ui/components/exsportia-icon/exsportia-icon'
import {GradientLoader} from '../../ui/components/gradient-loader/gradient-loader'
import {LabeledValueRow} from '../../ui/components/key-value-row/labeled-value-row'
import {TextWithShowMore} from '../../ui/components/text-with-show-more/text-with-show-more'
import {TitledSection} from '../../ui/components/titeled-section/titled-section'
import {useLocale} from '../../utils/i18n'
import {useModalActions} from '../../utils/modals'
import {BenefitsList} from '../subscription-profile/benefits/benefits-list'
import {TabbedHeader} from './components/header'
import {MembershipCancelRequestSection} from './components/membership-cancel-request-section'
import {buildChips, getSubscriptionBookingAccess} from './utils'

type TabsSettings = {
  text: string
  isActive: boolean
  onClick: () => void
}[]

type LFunc = (key: TranslatorScope, params?: Record<string, unknown>) => string

const SubscriptionBookingProfileView: FC<{
  tabsSettings: TabsSettings
  subscription?: {
    name?: string
    description?: string
    amenitiesDiffer?: boolean
    amenities?: any[]
    termsAndConditions?: string
    duration?: number
  } | null
  subscriptionBooking?: {
    membershipId?: string
    reference?: string
    membershipData?: any
  } | null
  amenities: any[]
  l: LFunc
  isRollingSub: boolean
  duration: unknown
  startDate: string | null
  endDate: string | null
  chips: {text: string; color: string; textColor: string}[]
  isLoading: boolean
  subscriptionId?: string
  isMembershipEnded: boolean
  canUpdateSavedCard: boolean
  canPayNextCycle: boolean
  getUpdateCardDisabledReason: () => string
  getPayNextCycleDisabledReason: () => string
  navigateToSaveCard: () => void
  navigateToPayCycle: () => void
  openUnavailableActionModal: (reason: string) => void
}> = ({
  tabsSettings,
  subscription,
  subscriptionBooking,
  amenities,
  l,
  isRollingSub,
  duration,
  startDate,
  endDate,
  chips,
  isLoading,
  subscriptionId,
  isMembershipEnded,
  canUpdateSavedCard,
  canPayNextCycle,
  getUpdateCardDisabledReason,
  getPayNextCycleDisabledReason,
  navigateToSaveCard,
  navigateToPayCycle,
  openUnavailableActionModal,
}) => {
  const handleCopyReferenceClick = useCallback(
    async (reference?: string) => {
      try {
        await navigator.clipboard.writeText(reference || '')
        showMessage(l('referenceNumberCopied'), 'success')
      } catch {
        showMessage(l('error'), 'error')
      }
    },
    [l],
  )

  const handleUpdateCardClick = useCallback(() => {
    if (isLoading) {
      return
    }
    if (canUpdateSavedCard) {
      navigateToSaveCard()
      return
    }
    const reason = getUpdateCardDisabledReason()
    if (reason) {
      openUnavailableActionModal(reason)
    }
  }, [isLoading, canUpdateSavedCard, getUpdateCardDisabledReason, openUnavailableActionModal, navigateToSaveCard])

  const handlePayNextCycleClick = useCallback(() => {
    if (isLoading) {
      return
    }
    if (canPayNextCycle) {
      navigateToPayCycle()
      return
    }
    const reason = getPayNextCycleDisabledReason()
    if (reason) {
      openUnavailableActionModal(reason)
    }
  }, [isLoading, canPayNextCycle, getPayNextCycleDisabledReason, openUnavailableActionModal, navigateToPayCycle])

  const renderAboutSection = () => (
    <Box p={'16px'}>
      <Box mb={'16px'}>
        <P fontWeight={'bold'} fontSize={['h1', 'desktop-h1']}>
          {subscription?.name || <GradientLoader style={{width: '150px'}} />}
        </P>
      </Box>
      {subscription?.description && (
        <Box mb={'16px'}>
          <TextWithShowMore text={`${subscription.description}`} />
        </Box>
      )}
      <BenefitsList calculatedSubscription={subscriptionBooking?.membershipData} />
      {Boolean(amenities.length) && (
        <TitledSection title={l('facilities')}>
          <Amenities amenities={amenities} />
        </TitledSection>
      )}
      {subscription?.termsAndConditions && (
        <TitledSection title={l('termsConditions')}>
          <P fontSize={'body'}>{subscription.termsAndConditions}</P>
        </TitledSection>
      )}
    </Box>
  )

  const renderReferenceRow = () => (
    <>
      <LabeledValueRow label={l('reference')}>
        {subscriptionBooking?.reference ? (
          <Flex alignItems={'center'} gap={'8px'} cursor={'pointer'} onClick={() => handleCopyReferenceClick(subscriptionBooking.reference)}>
            <P fontWeight={'bold'} color={'success'}>
              {subscriptionBooking.reference}
            </P>
            <ExsportiaIcon icon={'copy'} w={16} h={16} color={'success'} />
          </Flex>
        ) : (
          <GradientLoader style={{width: '75px'}} />
        )}
      </LabeledValueRow>
      <DividerWithContent />
    </>
  )

  const renderStartDateRow = () => (
    <>
      <LabeledValueRow label={l('startDate')}>{startDate || <GradientLoader style={{width: '75px'}} />}</LabeledValueRow>
      <DividerWithContent />
    </>
  )

  const renderDurationRow = () => (
    <>
      <LabeledValueRow label={l('duration')}>
          {subscription ? (
            isRollingSub ? (
              l('monthly')
            ) : duration ? (
              l(`${(duration as TranslatorScope)}_single` as TranslatorScope, {
                value: subscription.duration,
                declension: [`${(duration as TranslatorScope)}_single_genitive`, `${(duration as TranslatorScope)}_plural`] as [
                  TranslatorScope,
                  TranslatorScope,
                ],
              })
            ) : (
              <GradientLoader style={{width: '75px'}} />
            )
          ) : (
            <GradientLoader style={{width: '75px'}} />
          )}
        </LabeledValueRow>
      <DividerWithContent />
    </>
  )

  const renderPeriodRow = () => (
    <LabeledValueRow label={l('period')}>
      {startDate && endDate ? `${startDate || ''} - ${endDate || ''}` : <GradientLoader style={{width: '150px'}} />}
    </LabeledValueRow>
  )

  const renderActions = () => (
    <Box mt={'16px'}>
      <Box title={!isLoading && !canUpdateSavedCard ? getUpdateCardDisabledReason() : undefined} style={!isLoading && !canUpdateSavedCard ? {opacity: 0.5, cursor: 'not-allowed'} : undefined}>
        <Button onClick={handleUpdateCardClick} type='secondary' disabled={isLoading} isRightIcon={!isLoading && !canUpdateSavedCard}>
          {l('update_saved_card', {caseAction: 'capitalize'})}
        </Button>
      </Box>
      <Box mt={'16px'} title={!isLoading && !canPayNextCycle ? getPayNextCycleDisabledReason() : undefined} style={!isLoading && !canPayNextCycle ? {opacity: 0.5, cursor: 'not-allowed'} : undefined}>
        <Button onClick={handlePayNextCycleClick} type='secondary' disabled={isLoading} isRightIcon={!isLoading && !canPayNextCycle}>
          {l('pay_next_cycle', {caseAction: 'capitalize'})}
        </Button>
      </Box>
      <Box mt={'16px'}>
        <MembershipCancelRequestSection subscriptionId={subscriptionId} isMembershipEnded={isMembershipEnded} />
      </Box>
    </Box>
  )

  return (
    <GridLayout header={<TabbedHeader tabsSettings={tabsSettings} />} withoutPadding={true}>
      <Box>{renderAboutSection()}</Box>
      <Box p={'16px'}>
        <SubscriptionPreviewCard id={subscriptionBooking?.membershipId} chips={chips} />
        <Box mt={'12px'}>
          {renderReferenceRow()}
          {renderStartDateRow()}
          {renderDurationRow()}
          {renderPeriodRow()}
        </Box>
        {renderActions()}
      </Box>
    </GridLayout>
  )
}

export const SubscriptionBookingProfilePage: FC = () => {
  const {l} = useLocale()
  const navigate = useNavigate()
  const {data: venue} = useVenue()
  const {values, setFieldValue} = useFormikContext<PaySubscriptionCycleFormType>()
  const {data: subscriptionBooking, isLoading: isBookingLoading} = useSubscriptionBooking(values.subscriptionBooking?.uid)
  const {data: subscription, isLoading: isSubscriptionLoading} = useSubscription(subscriptionBooking?.membershipId)
  const {isLoggedIn} = useAuthState()
  const {data: user} = useUser()
  // The list shows passes held by the user's contact too (memberId), not only those carrying their userId.
  const {data: member, isLoading: isMemberLoading, isError: isMemberError} = useQuery(
    MemberQueries.getMemberByVenueQuery({
      query: {venueId: venue?.uid, userId: user?.uid},
      enabled: Boolean(venue?.uid && user?.uid),
    }),
  )
  const [displayProfile, setDisplayProfile] = useState(true)
  const {openModal, closeModal} = useModalActions()

  const isRollingSub = subscription?.rolling ?? false
  const duration = useMemo(() => {
    if (!subscription) {
      return null
    }
    return SUBSCRIPTION_DURATION_TYPE_MAP[subscription.durationType].label
  }, [subscription])
  const startDate = subscriptionBooking?.startDate
    ? DateTime.fromMillis(subscriptionBooking.startDate).toLocaleString(DateTime.DATE_MED)
    : null
  const endDate = subscriptionBooking?.endDate
    ? DateTime.fromMillis(subscriptionBooking.endDate).toLocaleString(DateTime.DATE_MED)
    : null

  const amenities = (subscription?.amenitiesDiffer ? subscription.amenities : venue?.amenities) || []

  const access = getSubscriptionBookingAccess({
    booking: subscriptionBooking,
    userId: user?.uid,
    memberId: member?.uid,
    // No venue yet means the contact lookup has not started: still loading, never a verdict.
    isMemberLoading: isMemberLoading || !venue?.uid,
    isMemberError,
  })

  useEffect(() => {
    if (access !== 'notOwner') {
      setDisplayProfile(true)
      return
    }
    setDisplayProfile(false)
    navigate('../../../')
  }, [isLoggedIn, access, navigate])

  useEffect(() => {
    setFieldValue('cyclePayType', null).catch((e) => console.log(String(e)))
  }, [])

  const navigateToSaveCard = useCallback(() => {
    navigate('../set-payment-method')
    setFieldValue('cyclePayType', CyclePayTypeEnum.SAVE_CARD).catch((e) => console.log(String(e)))
  }, [navigate, setFieldValue])

  const navigateToPayCycle = useCallback(() => {
    navigate('../pay-cycle')
    setFieldValue('cyclePayType', CyclePayTypeEnum.PAY_NOW).catch((e) => console.log(String(e)))
  }, [navigate, setFieldValue])

  const tabsSettings = useMemo(
    () => [
      {
        text: l('about', {caseAction: 'capitalize'}),
        isActive: true,
        onClick: noop,
      },
      {
        text: l('billing', {caseAction: 'capitalize'}),
        isActive: false,
        onClick: () => navigate('../billing'),
      },
    ],
    [l, navigate],
  )

  const isLoading = isBookingLoading || isSubscriptionLoading
  const isActive = subscriptionBooking?.status === SubscriptionBookingStatusesEnum.ACTIVE
  const isFullyPaid = useMemo(
    () => Boolean(subscriptionBooking?.cycles.length) && subscriptionBooking?.cycles.every((c) => c.isPaid),
    [subscriptionBooking],
  )
  const isSubCancelled = subscriptionBooking?.subStatus === SubscriptionBookingStatusesEnum.CANCELLED
  const isSubExpired = subscriptionBooking?.subStatus === SubscriptionBookingStatusesEnum.EXPIRED

  const canUpdateSavedCard = Boolean(isActive)
  const canPayNextCycle = Boolean(!isSubCancelled && !isSubExpired) && !isFullyPaid

  const getUpdateCardDisabledReason = useCallback(() => {
    if (!canUpdateSavedCard) {
      return l('subscription_not_active')
    }
    return ''
  }, [canUpdateSavedCard, l])

  const getPayNextCycleDisabledReason = useCallback(() => {
    if (canPayNextCycle) {
      return ''
    }
    if (isSubCancelled) {
      return l('subscription_cancelled')
    }
    if (isSubExpired) {
      return l('subscription_expired')
    }
    return l('action_not_available')
  }, [canPayNextCycle, isSubCancelled, isSubExpired, l])
  const openUnavailableActionModal = useCallback(
    (reason: string) => {
      openModal({
        title: l('unavailable'),
        component: (
          <Box>
            <P>{reason}</P>
          </Box>
        ),
        settings: {
          footer: {
            submitButton: {
              settings: {
                width: 'max-content',
              },
            },
            cancelButton: {
              settings: {
                width: 'max-content',
              },
            },
          },
        },
        footerConfig: {
          submitButton: {
            text: l('get_subscription', {caseAction: 'capitalize'}),
            onClick: () => {
              if (venue?.uid && subscriptionBooking?.membershipId) {
                navigate(`/${venue.uid}/subscription/${subscriptionBooking.membershipId}`)
              }
              closeModal()
            },
          },
          cancelButton: {
            text: l('home'),
            onClick: () => {
              navigate(`/${venue?.uid || ''}`)
              closeModal()
            },
          },
        },
      })
    },
    [openModal, closeModal, l, venue, subscriptionBooking, navigate],
  )

  const chips = useMemo(() => {
    return buildChips(subscriptionBooking, l)
  }, [subscriptionBooking, l])

  if (!displayProfile) {
    return null
  }

  return (
    <SubscriptionBookingProfileView
      tabsSettings={tabsSettings}
      subscription={subscription}
      subscriptionBooking={subscriptionBooking}
      amenities={amenities}
      l={l}
      isRollingSub={isRollingSub}
      duration={duration}
      startDate={startDate}
      endDate={endDate}
      chips={chips}
      isLoading={isLoading}
      subscriptionId={subscriptionBooking?.uid}
      isMembershipEnded={isSubCancelled || isSubExpired}
      canUpdateSavedCard={canUpdateSavedCard}
      canPayNextCycle={canPayNextCycle}
      getUpdateCardDisabledReason={getUpdateCardDisabledReason}
      getPayNextCycleDisabledReason={getPayNextCycleDisabledReason}
      navigateToSaveCard={navigateToSaveCard}
      navigateToPayCycle={navigateToPayCycle}
      openUnavailableActionModal={openUnavailableActionModal}
    />
  )
}
